// src/modules/units/tyres/shared/tyre-import-run.js
//
// The import runner: sends validated rows to the API in small batches and reports a
// result per row. The API functions are INJECTED (fetchTyre, saveTyres), so the
// whole control flow — batching, the existence check, stop, auth loss, error
// wording — runs in the Node harness against fakes, with no network.
//
// Per batch (default 10 rows):
//   1. CHECK. GET /tyre/{id} for each row (concurrency-limited). This is the
//      authoritative existence check — the same check-then-write guard the New
//      Tyre form uses — and it runs at write time, so it is right even if the tyre
//      was created or deleted after the Review step was drawn.
//        found   + skip mode   → Skipped (nothing written)
//        found   + update mode → written as an update; tyreKm dropped when the
//                                server is already tracking distance
//        404                   → written as a create
//        other failure         → Failed (we never write blind)
//   2. WRITE. One saveTyres() call for the batch's writes. saveTyres (tyres.api.js)
//      posts each tyre and emits ONE TYRE_EVENTS.CHANGED for the batch, so an import
//      of 500 rows causes 50 refreshes of any listening view, not 500.
//
// A 401 at any point stops the run: every later request would fail the same way,
// and the user needs to sign in again, not read 500 identical row errors.
// shouldStop() is polled between batches, so Stop finishes the batch in flight
// (never abandons a half-sent batch) and leaves the rest Not sent.

import { hasDistanceTracking } from './tyre-metrics.js';
import { keepExistingOverSuggestions, suggestionNote } from './tyre-import-validate.js';
import { planCatalogueEntries } from './tyre-catalogue-match.js';

export const IMPORT_RESULT = Object.freeze({
  CREATED: 'created',
  UPDATED: 'updated',
  SKIPPED: 'skipped',
  FAILED: 'failed'
});

export const DEFAULT_CHUNK_SIZE = 10;
export const DEFAULT_CONCURRENCY = 4;

/** Run `worker` over `items` with at most `limit` in flight; results keep order. */
export async function mapLimit(items, limit, worker) {
  const out = new Array(items.length);
  let next = 0;
  const lanes = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await worker(items[i], i);
    }
  });
  await Promise.all(lanes);
  return out;
}

// ASP.NET-style validation body: { errors: { Field: ["msg", …] } }
function validationDetail(data) {
  const errs = data?.errors;
  if (!errs || typeof errs !== 'object') return '';
  return Object.entries(errs)
    .flatMap(([k, v]) => (Array.isArray(v) ? v : [v]).map((m) => `${k}: ${m}`))
    .join(' ');
}

/** A user-facing sentence for a failed write/check response. */
export function describeFailure(res = {}) {
  const code = res.data?.code || res.code;
  if (res.status === 409 && code === 'TYRE_SOFT_DELETED') {
    return 'This ID belongs to a deleted tyre. An administrator must restore that tyre before it can be imported again.';
  }
  if (res.status === 401) return 'Your session has expired — sign in again and retry.';
  if (res.status === 403) return 'You don’t have permission to save tyres for this customer.';
  if (res.status === 0 || res.status === undefined) return res.message || 'Network error — check your connection.';
  const detail = validationDetail(res.data);
  const base = res.message && res.message !== 'Tyre request failed.' ? res.message : `The server rejected the tyre (HTTP ${res.status}).`;
  return detail ? `${base.replace(/[.\s]+$/, '')} — ${detail}` : base;
}

async function safe(fn) {
  try {
    return await fn();
  } catch (e) {
    return { success: false, status: 0, message: e?.message || 'Network error — check your connection.', data: null };
  }
}

/**
 * @param {object[]} rows rows from buildImportRows (errors already filtered out)
 * @param {object} opts
 * @param {'update'|'skip'} opts.mode
 * @param {(id:string) => Promise<object>} opts.fetchTyre
 * @param {(payloads:object[], o:object) => Promise<object[]>} opts.saveTyres
 * @param {number} [opts.chunkSize]
 * @param {number} [opts.concurrency]
 * @param {(p:{done:number,total:number,results:Map}) => void} [opts.onProgress]
 * @param {() => boolean} [opts.shouldStop]
 * @returns {Promise<{results: Map<number,{status:string,message:string}>, stopped:boolean, authLost:boolean}>}
 *          results keyed by the row's sheet rowNumber
 */
export async function runImport(rows, opts = {}) {
  const {
    mode = 'update', fetchTyre, saveTyres,
    chunkSize = DEFAULT_CHUNK_SIZE, concurrency = DEFAULT_CONCURRENCY,
    onProgress, shouldStop
  } = opts;

  const results = new Map();
  const total = rows.length;
  let done = 0;
  let authLost = false;
  let stopped = false;

  const record = (row, status, message = '') => { results.set(row.rowNumber, { status, message }); };

  for (let start = 0; start < rows.length; start += chunkSize) {
    if (shouldStop && shouldStop()) { stopped = true; break; }
    const chunk = rows.slice(start, start + chunkSize);

    // 1. CHECK
    const writes = [];
    const checks = await mapLimit(chunk, concurrency, (row) => safe(() => fetchTyre(row.id)));
    chunk.forEach((row, i) => {
      const res = checks[i] || {};
      if (res.status === 401) { authLost = true; record(row, IMPORT_RESULT.FAILED, describeFailure(res)); return; }
      if (res.success && res.data) {
        if (mode === 'skip') { record(row, IMPORT_RESULT.SKIPPED, 'Already on file — skipped.'); return; }
        const payload = { ...row.payload };
        const notes = [];
        if (payload.tyreKm !== undefined && hasDistanceTracking(res.data)) {
          delete payload.tyreKm;
          notes.push('Opening distance left unchanged — distance is already being tracked.');
        }
        // Authoritative copy of the Review rule: a catalogue suggestion never
        // replaces a value the tyre already has (the form's edit behaviour).
        const kept = keepExistingOverSuggestions(payload, row.suggested || [], res.data);
        if (kept.length) notes.push(suggestionNote(kept));
        const note = notes.join(' ');
        writes.push({ row, payload, kind: IMPORT_RESULT.UPDATED, note });
        return;
      }
      if (res.status === 404) { writes.push({ row, payload: { ...row.payload }, kind: IMPORT_RESULT.CREATED, note: '' }); return; }
      record(row, IMPORT_RESULT.FAILED, `Couldn’t check whether this tyre already exists: ${describeFailure(res)}`);
    });

    // 2. WRITE (skipped entirely once auth is lost — it would only fail again)
    if (writes.length && !authLost) {
      let saved = await safe(() => saveTyres(writes.map((w) => w.payload), { concurrency, reason: 'import' }));
      if (!Array.isArray(saved)) saved = writes.map(() => saved); // whole-batch failure
      writes.forEach((w, i) => {
        const res = saved[i] || { success: false, status: 0 };
        if (res.status === 401) authLost = true;
        if (res.success) record(w.row, w.kind, w.note);
        else record(w.row, IMPORT_RESULT.FAILED, describeFailure(res));
      });
    } else if (writes.length) {
      writes.forEach((w) => record(w.row, IMPORT_RESULT.FAILED, 'Not sent — your session expired.'));
    }

    done += chunk.length;
    if (typeof onProgress === 'function') onProgress({ done, total, results });
    if (authLost) break;
  }

  return { results, stopped, authLost };
}

/**
 * Phase 0 — add the missing tyre catalogue entries, before any tyre is written.
 *
 * Re-reads the catalogue and RE-PLANS against it rather than trusting the plan the
 * Review step showed: the same write-time rule as the tyres' GET check, so an
 * entry someone added in the meantime is matched, not duplicated. Entries are
 * created a few at a time (the catalogue API emits CATALOGUE_CHANGED per entry;
 * nothing that listens to it is mounted during an import).
 *
 * A catalogue failure never blocks the tyres: the catalogue is a record of specs
 * and defaults, not something the tyre record depends on.
 *
 * @param {object[]} rows the rows about to be imported (create/update are used)
 * @param {object} opts
 * @param {() => Promise<object>} opts.fetchCatalogue   { success, data: { results } }
 * @param {(payload:object) => Promise<object>} opts.createEntry
 * @returns {Promise<{ok:boolean, message?:string, planned:number, created:object[], failed:object[], authLost:boolean}>}
 */
export async function runCatalogue(rows, { fetchCatalogue, createEntry, concurrency = 2 } = {}) {
  const out = { ok: true, planned: 0, created: [], failed: [], authLost: false };
  const fresh = await safe(fetchCatalogue);
  if (!fresh.success) {
    return { ...out, ok: false, authLost: fresh.status === 401, message: `The tyre catalogue couldn’t be read, so no entries were added: ${describeFailure(fresh)}` };
  }
  const entries = Array.isArray(fresh.data?.results) ? fresh.data.results : [];
  const items = rows
    .filter((r) => r.action === 'create' || r.action === 'update')
    .map((r) => ({ rowNumber: r.rowNumber, payload: r.payload }));
  const plan = planCatalogueEntries(items, entries);
  out.planned = plan.length;

  const results = await mapLimit(plan, concurrency, (p) => safe(() => createEntry(p.payload)));
  plan.forEach((p, i) => {
    const res = results[i] || {};
    if (res.success) { out.created.push({ label: p.label, entry: res.data, rowNumbers: p.rowNumbers }); return; }
    if (res.status === 401) out.authLost = true;
    const reason = res.status === 403
      ? 'Administrator access is required to add catalogue entries.'
      : describeFailure(res);
    out.failed.push({ label: p.label, reason, rowNumbers: p.rowNumbers });
  });
  return out;
}

/** Counts by result status, plus how many runnable rows were never sent. */
export function resultCounts(rows = [], results = new Map()) {
  const c = { created: 0, updated: 0, skipped: 0, failed: 0, notSent: 0 };
  for (const r of rows) {
    const res = results.get(r.rowNumber);
    if (!res) c.notSent++;
    else c[res.status]++;
  }
  return c;
}
